import {
    Box,
    DialogContentText,
    FormHelperText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { mdiDotsVertical } from '@mdi/js';

import { GroupConditionGroupExtendedListItem } from 'user/components/GroupConditionFilter/GroupConditionGroupExtendedListItem';
import {
    IContentTypeValue,
    IExtraCategoryOption,
    ExtraCategoryFiltersDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { Loader } from 'common/components/Loader';
import { getExtraCategoryFilterItems } from 'organisation/utils/extraCategory';
import { getSelectedGroupConditionFilters } from 'user/utils/groups';
import { AddButton } from 'common/components/Button/AddButton';
import {
    IGroupConditionInput,
    IOldGroupConditionInput,
    TGroupListItem,
} from 'user/types';
import { GroupSelectDrawer } from 'user/components/GroupSelectDrawer';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { IconButton } from 'common/components/IconButton';
import { Divider } from 'common/components/Divider';

interface IProps {
    error?: string;
    groupConditionInputs?: (IGroupConditionInput | IOldGroupConditionInput)[];
}

export const GroupConditionFilterExtended = ({
    error,
    groupConditionInputs,
}: IProps) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();

    const {
        data: extraCategoryFiltersData,
        loading: extraCategoryFiltersLoading,
        error: extraCategoryFiltersError,
    } = useQuery(ExtraCategoryFiltersDocument, {
        variables: { contentType: IContentTypeValue.Group },
    });

    useEffect(() => {
        if (extraCategoryFiltersError) {
            showApolloError(extraCategoryFiltersError);
        }
    }, [extraCategoryFiltersError, showApolloError]);
    const [selectedFilterIndex, setSelectedFilterIndex] = useState<
        number | undefined
    >();
    const [selectDrawerOpen, setSelectDrawerOpen] = useState(false);
    const { setFieldValue } = useFormikContext();
    const [showOnDeleteAlert, setShowOnDeleteAlert] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<TGroupListItem[]>([]);

    // Checks each group condition input if it has any min/max filters
    const initialMinMaxFiltersOn = useMemo(
        () =>
            groupConditionInputs?.map(
                (groupConditionInput) =>
                    !!groupConditionInput.minMaxFilters?.length
            ),
        [groupConditionInputs]
    );

    const [minMaxFiltersOn, setMinMaxFiltersOn] = useState(
        initialMinMaxFiltersOn || []
    );

    const { extraCategoriesFilters } = extraCategoryFiltersData || {};

    // Only use choice type filters
    const filteredCategoryFilters = extraCategoriesFilters?.filter(
        (categoryFilter) =>
            categoryFilter.categoryType === IExtraCategoryOption.Choice
    );

    const loading = extraCategoryFiltersLoading;

    const handleSelectGroup = (group: TGroupListItem) => {
        const newGroups = [...selectedGroups, group].sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        setSelectedGroups(newGroups);
    };

    const handleAddGroups = (selectedGroups: TGroupListItem[]) => {
        // If existing group condition is being edited, replace it with new values
        if (selectedFilterIndex !== undefined && groupConditionInputs?.length) {
            const newGroupConditionInputs = [...groupConditionInputs];

            newGroupConditionInputs[selectedFilterIndex] = {
                ...groupConditionInputs[selectedFilterIndex],
                groups: selectedGroups,
            };

            setFieldValue('groupConditions', newGroupConditionInputs);
        } else {
            const newGroupConditionInput = {
                groups: selectedGroups,
            };

            setFieldValue(
                'groupConditions',
                groupConditionInputs?.length
                    ? [...groupConditionInputs, newGroupConditionInput]
                    : [newGroupConditionInput]
            );
        }

        setSelectedFilterIndex(undefined);
        setSelectDrawerOpen(false);
    };

    const handleClickDelete = (groupConditionIndex: number) => {
        const newGroupConditions = groupConditionInputs?.filter(
            (_groupConditionInput, index) => index !== groupConditionIndex
        );

        setFieldValue('groupConditions', newGroupConditions);
    };

    const addGroupCondition = () => {
        // Clear selected groups when adding a new group condition
        setSelectedGroups([]);

        setSelectDrawerOpen(true);
    };

    const addedGroups = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        return groupConditionInputs[selectedFilterIndex]
            .groups as TGroupListItem[];
    }, [selectedFilterIndex, groupConditionInputs]);

    const selectedFilters = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        const { extraCategoryValues } =
            groupConditionInputs[selectedFilterIndex];

        return getSelectedGroupConditionFilters(
            extraCategoryValues,
            getExtraCategoryFilterItems(filteredCategoryFilters)
        );
    }, [selectedFilterIndex, groupConditionInputs, extraCategoriesFilters]);

    const drawerOpen = selectDrawerOpen || selectedFilterIndex !== undefined;

    const noneOfTheGroupsHaveMinMaxFilters = minMaxFiltersOn.every(
        (minMaxFilter) => !minMaxFilter
    );

    return (
        <Box>
            {loading && (
                <Box sx={{ height: 40, position: 'relative', mb: 2 }}>
                    <Loader />
                </Box>
            )}

            {!loading && (
                <>
                    <AddButton
                        aria-label={translate('groupConditionFilter.addButton')}
                        onClick={addGroupCondition}
                    >
                        {translate('groupConditionFilter.addButton')}
                    </AddButton>

                    {error && (
                        <FormHelperText error sx={{ mt: 2 }}>
                            {error}
                        </FormHelperText>
                    )}

                    {!!groupConditionInputs?.length && (
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            {groupConditionInputs.map(
                                (
                                    groupConditionInput,
                                    groupConditionInputIndex
                                ) => {
                                    const { extraCategoryValues, groups } =
                                        groupConditionInput;

                                    // If there is a group that the user does not have permission to update (is group manager of),
                                    // the user cannot delete the group condition
                                    const canDelete = !(
                                        groups as TGroupListItem[]
                                    ).some(({ permissions }) => {
                                        const groupPermissions = permissions
                                            ? JSON.parse(permissions)
                                            : undefined;

                                        return (
                                            !groupPermissions ||
                                            !groupPermissions.canUpdate
                                        );
                                    });

                                    const currentGroupConditionHasMinMaxFilters =
                                        minMaxFiltersOn[
                                            groupConditionInputIndex
                                        ];

                                    return (
                                        <Box
                                            key={`groupCondition-${groupConditionInputIndex}`}
                                        >
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    pb: 1,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        py: 2,
                                                    }}
                                                >
                                                    <Typography fontWeight="bold">
                                                        {translate(
                                                            'groupConditionFilter.groupConditionHeading'
                                                        )}
                                                    </Typography>

                                                    <DropdownMenu
                                                        anchor={
                                                            <IconButton
                                                                color="inherit"
                                                                iconPath={
                                                                    mdiDotsVertical
                                                                }
                                                                iconSize="2.4rem"
                                                                sx={{
                                                                    ml: 'auto',
                                                                }}
                                                            />
                                                        }
                                                        placement="bottom-end"
                                                    >
                                                        <MenuItem
                                                            onClick={() => {
                                                                const selected =
                                                                    groupConditionInputs[
                                                                        groupConditionInputIndex
                                                                    ]
                                                                        .groups as TGroupListItem[];

                                                                // Set the groups of the currently editing group condition
                                                                if (selected) {
                                                                    setSelectedGroups(
                                                                        selected
                                                                    );
                                                                }

                                                                setSelectedFilterIndex(
                                                                    groupConditionInputIndex
                                                                );
                                                            }}
                                                        >
                                                            {translate('edit')}
                                                        </MenuItem>

                                                        <MenuItem
                                                            onClick={() => {
                                                                if (canDelete) {
                                                                    handleClickDelete(
                                                                        groupConditionInputIndex
                                                                    );

                                                                    return;
                                                                }

                                                                setShowOnDeleteAlert(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            {translate(
                                                                'delete'
                                                            )}
                                                        </MenuItem>

                                                        {
                                                            // Only one group can have min/max filters at the same time, show the toggle when:
                                                            // - None of the groups have min/max filters, so every group condition has the toggle
                                                            // - The current group condition has min/max filters on, only current group condition has the toggle
                                                        }
                                                        {(noneOfTheGroupsHaveMinMaxFilters ||
                                                            currentGroupConditionHasMinMaxFilters) && (
                                                            <MenuItem
                                                                onClick={() => {
                                                                    const newMinMaxFiltersOn =
                                                                        [
                                                                            ...minMaxFiltersOn,
                                                                        ];

                                                                    const currentMinMaxFiltersOn =
                                                                        newMinMaxFiltersOn[
                                                                            groupConditionInputIndex
                                                                        ];

                                                                    // Toggle the min/max filters on/off
                                                                    newMinMaxFiltersOn[
                                                                        groupConditionInputIndex
                                                                    ] =
                                                                        !currentMinMaxFiltersOn;

                                                                    setMinMaxFiltersOn(
                                                                        newMinMaxFiltersOn
                                                                    );

                                                                    // If min/max filters are turned off, clear the min/max filters
                                                                    if (
                                                                        !newMinMaxFiltersOn[
                                                                            groupConditionInputIndex
                                                                        ]
                                                                    ) {
                                                                        const newMinMaxFilters =
                                                                            groupConditionInput.minMaxFilters?.map(
                                                                                (
                                                                                    minMaxFilter
                                                                                ) => ({
                                                                                    ...minMaxFilter,
                                                                                    min: null,
                                                                                    max: null,
                                                                                })
                                                                            );

                                                                        setFieldValue(
                                                                            `groupConditions[${groupConditionInputIndex}].minMaxFilters`,
                                                                            newMinMaxFilters
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {translate(
                                                                    `groupConditionFilter.${minMaxFiltersOn[groupConditionInputIndex] ? 'minMaxFiltersOff' : 'minMaxFiltersOn'}`
                                                                )}
                                                            </MenuItem>
                                                        )}
                                                    </DropdownMenu>
                                                </Box>

                                                <Divider sx={{ mb: 1 }} />

                                                {groups.map(
                                                    (group, groupIndex) => (
                                                        <Box
                                                            key={
                                                                (
                                                                    group as TGroupListItem
                                                                ).id
                                                            }
                                                        >
                                                            <GroupConditionGroupExtendedListItem
                                                                extraCategoryValues={
                                                                    extraCategoryValues
                                                                }
                                                                group={
                                                                    group as TGroupListItem
                                                                }
                                                                groupConditionInput={
                                                                    groupConditionInput
                                                                }
                                                                groupConditionInputIndex={
                                                                    groupConditionInputIndex
                                                                }
                                                                showMinMaxFilters={
                                                                    minMaxFiltersOn[
                                                                        groupConditionInputIndex
                                                                    ]
                                                                }
                                                            />

                                                            {groupIndex <
                                                                groups.length -
                                                                    1 && (
                                                                <Divider
                                                                    sx={{
                                                                        my: 1,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </Box>

                                            {groupConditionInputIndex <
                                                groupConditionInputs.length -
                                                    1 && (
                                                <Divider
                                                    sx={{ mt: 2 }}
                                                    variant="dashed"
                                                >
                                                    <Typography>
                                                        {translate(
                                                            'groupConditionFilter.conditionAnd'
                                                        )}
                                                    </Typography>
                                                </Divider>
                                            )}
                                        </Box>
                                    );
                                }
                            )}
                        </Stack>
                    )}

                    <AlertDialog
                        actions={
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setShowOnDeleteAlert(false);
                                }}
                            >
                                {translate('close')}
                            </Button>
                        }
                        open={showOnDeleteAlert}
                        title={translate(
                            'offerEventForm.cantDeleteGroupCondition.title'
                        )}
                    >
                        <DialogContentText color="text.primary" variant="body2">
                            {translate(
                                'offerEventForm.cantDeleteGroupCondition.text'
                            )}
                        </DialogContentText>
                    </AlertDialog>

                    <GroupSelectDrawer
                        addedGroups={addedGroups}
                        initialSelectedFilters={selectedFilters}
                        open={drawerOpen}
                        selectedGroups={selectedGroups}
                        setSelectedGroups={setSelectedGroups}
                        onAddGroups={(groups) => handleAddGroups(groups || [])}
                        onClose={() => {
                            setSelectedFilterIndex(undefined);
                            setSelectDrawerOpen(false);
                        }}
                        onRemoveGroup={(group) => {
                            setSelectedGroups(
                                selectedGroups.filter(
                                    (selectedGroup) =>
                                        selectedGroup.id !== group.id
                                )
                            );
                        }}
                        onSelectGroup={handleSelectGroup}
                    />
                </>
            )}
        </Box>
    );
};
